import type { RedemptionRequest } from '../../services/redemptions.service';
import type { UpcomingMaturity } from '../../types/finance';
import { calendarDate } from './enteringFund.helpers';

/**
 * A series held to the end of its term (owner, 2026-11-01; the CETES series): no early
 * withdrawal. Its term renews by itself; the partner leaves by a notice of non-renewal given
 * before the term ends, which covers the whole holding (never an amount, S-61), and is paid at
 * the end of that term with no charge. Every date here is
 * the one funds gave (`open-terms`, the notice's exit day): the portal never works one out.
 */
export const NO_EARLY_EXIT_NOTICE =
  'This series has no early withdrawal. Its term renews by itself. If you do not want it to renew, '
  + 'tell us before the term ends: your whole holding is paid at the end of the current term, with no charge. '
  + 'You can withdraw that notice only until the last day it could be given; after that the decision is fixed.';

/** The terms funds says this holding is in today; null when funds could not say them. */
export function termsOf(
  productId: string,
  terms: readonly UpcomingMaturity[] | null,
): UpcomingMaturity[] | null {
  return terms === null ? null : terms.filter((term) => term.product_id === productId);
}

/** The action's label: the term's last day when the holding is in one term, as funds says it. */
export function nonRenewalLabel(terms: readonly UpcomingMaturity[] | null): string {
  if (terms !== null && terms.length === 1) {
    return `Don't renew: be paid at the end of this term (${calendarDate(terms[0].maturity_date)})`;
  }
  return "Don't renew: be paid at the end of the current term";
}

/** A notice of non-renewal: a redemption request that kept the exit day funds gave it. */
export type Notice = RedemptionRequest & { exit_on: string };

/** The partner's notices of non-renewal of this holding that are still open: waiting for their
 *  day, or with a rest waiting for a later one. */
export function waitingNotices(
  requests: readonly RedemptionRequest[],
  productId: string,
): Notice[] {
  return requests.filter(
    (r): r is Notice =>
      r.product_id === productId
      && (r.status === 'requested' || r.status === 'partially_filled')
      && r.exit_on != null,
  );
}

/** A notice the fund has not answered yet: its last day to withdraw it is not known, so it
 *  cannot be withdrawn yet (owner, 2026-11-02; the day is the fund's answer). */
export const NOTICE_BEING_REGISTERED =
  'Your notice not to renew is being registered. Once it is, you will see here until when you can withdraw it.';

/** Whether the fund has not answered the notice yet. */
export function beingRegistered(notice: Notice): boolean {
  return !notice.withdrawable_until;
}

/** Until when a notice can still be withdrawn, as the server stored it; null once it cannot. */
export function withdrawableLine(notice: Notice): string | null {
  if (notice.cancellable !== true || !notice.withdrawable_until) return null;
  return `You can withdraw this notice until ${calendarDate(notice.withdrawable_until)}.`;
}

/** What a partner reads about a notice they gave: the day funds gave it. */
export function noticeLine(notice: Notice): string {
  const day = calendarDate(notice.exit_on);
  return `You asked not to renew. Your money leaves this series on ${day}, at the end of the current term, with no charge.`;
}
